<script lang="ts" setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { UiBtn, UiScrollWrap, UiImg } from '@shared/ui';
import type { MobileMenuEmits } from '../types';
import type { PropsMenu } from './types';
import { menuContent, bannerConfig } from './content';
import { useRoute, useRuntimeConfig } from '#imports';

const runtimeConfig = useRuntimeConfig();
const imgUrlPrefix = runtimeConfig.public.imgUrlPrefix;
const route = useRoute();
const emit = defineEmits<MobileMenuEmits>();

const props = withDefaults(defineProps<PropsMenu>(), {
  isOpen: false,
  activeMenu: null,
});

const menuWrapRef = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(typeof document !== 'undefined' ? document.body : null);

const banners = computed(() => {
  type BannerKey = keyof typeof bannerConfig;
  const key = props.activeMenu as BannerKey;
  return bannerConfig[key] ?? bannerConfig.default;
});

const content = computed(() => {
  if (!props.activeMenu || !menuContent[props.activeMenu]) {
    return { left: [], right: [] };
  }
  return menuContent[props.activeMenu];
});

const leftNav = computed(() => content.value?.left || []);
const rightNav = computed(() => content.value?.right || []);

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (
    menuWrapRef.value
    && !menuWrapRef.value.contains(target)
    && !target.closest('[data-menu-opener]')
  ) {
    closeMenu();
  }
};

const closeMenu = () => emit('close');

watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    isLocked.value = true;
    nextTick(() => {
      document.addEventListener('click', handleClickOutside);
    });
  }
  else {
    isLocked.value = false;
    document.removeEventListener('click', handleClickOutside);
  }
});

watch(
  () => route.path,
  (newPath, oldPath) => {
    if (newPath !== oldPath) closeMenu();
  },
);

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  isLocked.value = false;
});
</script>

<template>
  <Transition name="fade">
    <article
      v-show="isOpen"
      class="fixed -z-50 top-[7.35rem] left-0 right-0 bottom-0 w-full flex flex-col items-center outline-0 backdrop-blur-sm bg-gray-950/20 px-7.5"
      @click.self="closeMenu"
    >
      <div
        class="max-h-full max-w-[1128px] w-full relative flex justify-center pt-1 max-[744px]:pt-0"
      >
        <div
          class=" relative h-full w-full flex flex-col bg-white rounded-3xl overflow-hidden max-[744px]:rounded-none pt-0"
        >
          <main class="w-full h-full overflow-auto pt-2 px-5 pb-6 max-[744px]:pt-2 max-[744px]:px-4 max-[744px]:pb-4">
            <UiScrollWrap>
              <div class="flex flex-row justify-between text-gray-900">
                <div class="grid grid-cols-2 gap-26 max-[1050px]:gap-16 max-[1000px]:gap-6 max-[950px]:gap-0 pt-3 text-gray-900">
                  <div class="flex flex-col gap-6">
                    <div
                      v-if="activeMenu === 'Services'"
                      class="max-w-[302px] flex flex-col gap-3"
                    >
                      <NuxtLink
                        v-for="(block, index) in leftNav[0]?.blocks"
                        :key="'service-' + index"
                        :to="block.link"
                        class="flex flex-col gap-2 p-3 rounded-2xl hover:bg-gray-10 active:bg-gray-50 active:duration-75 transition"
                      >
                        <p class="font-bold text-sm">{{ block.title }}</p>
                        <p class="flex flex-col text-sm gap-3">
                          {{ block.label }}
                        </p>
                      </NuxtLink>
                    </div>

                    <template v-else>
                      <template
                        v-for="(blockGroup, groupIndex) in leftNav"
                        :key="`left-group-${groupIndex}`"
                      >
                        <div
                          v-for="(block, blockIndex) in blockGroup.blocks"
                          :key="`left-block-${blockIndex}`"
                          class="flex flex-col gap-2"
                        >
                          <p class="font-bold text-sm pl-3">
                            {{ block.title }}
                          </p>
                          <ul class="flex flex-col gap-2 transition">
                            <li
                              v-for="(item, itemIndex) in block.blocks"
                              :key="`left-item-${itemIndex}`"
                              class="transition"
                            >
                              <NuxtLink
                                :to="item.link"
                                class="flex flex-col gap-2 py-1 rounded-lg hover:bg-gray-10 active:bg-gray-50 active:duration-75 transition pl-3"
                              >
                                <span class="text-sm">{{ item.label }}</span>
                              </NuxtLink>
                            </li>
                          </ul>
                          <NuxtLink
                            v-if="block.footerButton"
                            :to="block.footerButton.link"
                            class="text-blue-600 active:text-blue-800 active:duration-75 text-sm underline hover:no-underline mt-2 pl-3"
                          >
                            {{ block.footerButton.label }}
                          </NuxtLink>
                        </div>
                      </template>
                    </template>
                  </div>

                  <div class="flex flex-col gap-6">
                    <template
                      v-for="(blockGroup, groupIndex) in rightNav"
                      :key="`right-group-${groupIndex}`"
                    >
                      <div
                        v-for="(block, blockIndex) in blockGroup.blocks"
                        :key="`right-block-${blockIndex}`"
                        class="flex flex-col gap-2"
                      >
                        <h3 class="font-bold text-sm pl-2.5">
                          {{ block.title }}
                        </h3>
                        <ul class="flex flex-col gap-2">
                          <li
                            v-for="(item, itemIndex) in block.blocks"
                            :key="`right-item-${itemIndex}`"
                          >
                            <NuxtLink
                              :to="item.link"
                              class="flex flex-col gap-2 py-1 rounded-lg hover:bg-gray-10 active:bg-gray-50 active:duration-75 transition pl-2.5"
                            >
                              <span class="text-sm">{{ item.label }}</span>
                            </NuxtLink>
                          </li>
                        </ul>
                        <NuxtLink
                          v-if="block.footerButton"
                          :to="block.footerButton.link"
                          class="text-blue-600 active:text-blue-800 active:duration-75 text-sm underline hover:no-underline mt-2 pl-2.5"
                        >
                          {{ block.footerButton.label }}
                        </NuxtLink>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="max-[892px]:hidden pt-3">
                  <ul class="flex flex-col w-[362px] gap-6">
                    <li
                      v-for="(banner, idx) in banners"
                      :key="idx"
                      class="list-none flex flex-col gap-2"
                    >
                      <NuxtLink
                        tag="a"
                        :to="banner.link"
                        external
                      >
                        <UiImg
                          :src="`${imgUrlPrefix}/${banner.img}`"
                          :alt="banner.alt"
                          class="rounded-[8px]"
                          :width="362"
                        />
                      </NuxtLink>
                      <UiBtn
                        tag="a"
                        type="text"
                        class="w-[99%] !rounded-[0px] hover:!text-purple-800 focus:!outline-none focus:!ring-0"
                        :label="banner.buttonText"
                        :href="banner.link"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </UiScrollWrap>
          </main>
        </div>
      </div>
    </article>
  </Transition>
</template>
